import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { adminApi, errorMessage } from "../lib/api";
import { PhoneVerify } from "../components/landing/PhoneVerify";
import { WalletCard } from "../components/employer/WalletCard";
import { WorkerSearch } from "../components/employer/WorkerSearch";
import { Placements } from "../components/employer/Placements";
import { EmployerMatches } from "../components/employer/EmployerMatches";
import { WhatsAppAlerts } from "../components/employer/WhatsAppAlerts";
import { EmployeeStats } from "../components/employer/EmployeeStats";
import { Applications } from "../components/employer/Applications";
import { MyVacancies } from "../components/employer/MyVacancies";
import { usePricing } from "../components/landing/usePricing";

const Login = ({ onLogin }) => {
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  useEffect(() => {
    if (!token) return;
    adminApi.post("/employer/login", { phone, phone_verification_token: token })
      .then((r) => onLogin(r.data)).catch((e) => { toast.error(errorMessage(e)); setToken(""); });
  }, [token, phone, onLogin]);
  return (
    <div data-testid="employer-login" className="mx-auto mt-16 max-w-md space-y-5 border border-white/10 bg-[#1E293B]/60 p-8">
      <h1 className="font-heading text-2xl font-extrabold text-white">Employer login</h1>
      <p className="text-sm text-slate-400">Use the HR phone number you registered with. We'll send an SMS OTP.</p>
      <PhoneVerify prefix="employer-login" label="HR Phone" phone={phone} token={token}
        onPhone={(v) => { setPhone(v); setToken(""); }} onToken={setToken} />
    </div>
  );
};

export default function EmployerPortal() {
  const [account, setAccount] = useState(undefined);
  const [refresh, setRefresh] = useState(0);
  const pricing = usePricing();
  const reload = useCallback(() => {
    adminApi.get("/employer/me").then((r) => setAccount(r.data)).catch(() => setAccount(null));
    setRefresh((x) => x + 1);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const logout = () => adminApi.post("/employer/logout").finally(() => setAccount(null));

  return (
    <div className="min-h-screen bg-[#0B1120] px-4 pb-20 font-body text-slate-100 sm:px-6">
      <header className="mx-auto flex max-w-6xl items-center justify-between py-5">
        <a href="/" data-testid="employer-portal-home" className="flex items-center gap-2.5">
          <span className="font-heading text-lg font-extrabold text-white">Balangir Group <span className="font-mono text-xs font-normal text-slate-500">/ employer</span></span>
        </a>
        {account && (
          <button onClick={logout} data-testid="employer-logout-button" className="flex items-center gap-2 text-sm text-slate-300 hover:text-orange-400">
            <LogOut className="h-4 w-4" />Log out
          </button>
        )}
      </header>
      {account === null && <Login onLogin={(a) => { setAccount(a); setRefresh((x) => x + 1); }} />}
      {account && (
        <main className="mx-auto max-w-6xl space-y-8">
          <h1 data-testid="employer-company-name" className="font-heading text-3xl font-extrabold text-white">{account.company_name}</h1>
          {account.kyc_notice && (
            <div data-testid="employer-kyc-approved" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4">
              <p className="text-sm font-semibold text-emerald-600">Your company KYC is approved! You can now unlock contacts and hire employees.</p>
              <button onClick={() => adminApi.post("/employer/kyc-notice/seen").then(reload).catch(() => {})} data-testid="employer-kyc-approved-ok" className="rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700">OK</button>
            </div>
          )}
          {account.kyc_approved === false && <p data-testid="employer-kyc-pending" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm font-semibold text-amber-600">Your company KYC is waiting for Balangir Group approval. You can see employees now and unlock contacts / hire after approval.</p>}
          <WalletCard account={account} refresh={refresh} onChange={reload} />
          <WhatsAppAlerts />
          <WorkerSearch onChange={reload} refresh={refresh} />
          <EmployerMatches refresh={refresh} fee={pricing.mode === "free" ? 0 : pricing.unlock_fee} onChange={reload} />
          <MyVacancies refresh={refresh} />
          <Applications refresh={refresh} fee={pricing.mode === "free" ? 0 : pricing.unlock_fee} onChange={reload} />
          <EmployeeStats />
          <Placements refresh={refresh} onChange={reload} />
        </main>
      )}
    </div>
  );
}
